import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import './Dashboard.css';

const STATUS_COPY = {
  en: { open: 'Open', in_progress: 'In progress', completed: 'Completed', closed: 'Closed' },
  zh: { open: '招募中', in_progress: '进行中', completed: '已完成', closed: '已关闭' },
};

function yuan(value) {
  return `¥${Number(value || 0).toLocaleString()}`;
}

function dateText(value, isZh) {
  if (!value) return isZh ? '暂无时间' : 'No date';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' });
}

export default function Dashboard() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const [stats, setStats] = useState({ total: 0, users: 0, pool: 0, paid: 0 });
  const [tasks, setTasks] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, frozen: 0, recent: [] });
  const [ownedServices, setOwnedServices] = useState([]);
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [sellerOrders, setSellerOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      api.get('/tasks/stats'),
      api.get('/tasks?limit=4'),
      api.get('/payments/wallet'),
      api.get('/marketplace/services/mine'),
      api.get('/payments/orders', { params: { role: 'buyer' } }),
      api.get('/payments/orders', { params: { role: 'seller' } }),
    ]).then(([statsRes, tasksRes, walletRes, servicesRes, buyerOrdersRes, sellerOrdersRes]) => {
      if (!active) return;
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data || {});
      if (tasksRes.status === 'fulfilled') setTasks(tasksRes.value.data?.tasks || []);
      if (walletRes.status === 'fulfilled') setWallet(walletRes.value.data || { balance: 0, frozen: 0, recent: [] });
      if (servicesRes.status === 'fulfilled') setOwnedServices(servicesRes.value.data?.services || []);
      if (buyerOrdersRes.status === 'fulfilled') setBuyerOrders(buyerOrdersRes.value.data?.orders || []);
      if (sellerOrdersRes.status === 'fulfilled') setSellerOrders(sellerOrdersRes.value.data?.orders || []);
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const copy = useMemo(() => ({
    greeting: isZh ? '欢迎回来' : 'Welcome back',
    subtitle: isZh ? '今天优先处理最重要的工作、资金和消息。' : 'Focus on your most important work, money, and messages today.',
    publish: isZh ? '发布新需求' : 'Post requirement',
    viewAll: isZh ? '查看全部' : 'View all',
    recent: isZh ? '最近需求' : 'Recent requirements',
    finance: isZh ? '资金概览' : 'Finance',
    activity: isZh ? '最近动态' : 'Recent activity',
    totalTasks: isZh ? '平台需求' : 'Open requirements',
    creators: isZh ? '发布者' : 'Clients',
    pool: isZh ? '奖金池' : 'Budget pool',
    paid: isZh ? '已支付' : 'Paid out',
    balance: isZh ? '可用余额' : 'Available',
    escrow: isZh ? '托管中' : 'In escrow',
    buyingTitle: isZh ? '购买视角' : 'Buying',
    sellingTitle: isZh ? '发布视角' : 'Selling',
    orders: isZh ? '订单' : 'Orders',
    active: isZh ? '进行中' : 'Active',
    completed: isZh ? '已完成' : 'Completed',
    services: isZh ? '服务' : 'Services',
    manageBuying: isZh ? '管理购买' : 'Manage buying',
    manageSelling: isZh ? '管理发布' : 'Manage selling',
    empty: isZh ? '暂无需求，先发布一个需求。' : 'No requirements yet. Post a requirement to get started.',
    noActivity: isZh ? '暂无新的资金或消息动态。' : 'No new money or message activity.',
  }), [isZh]);

  const buyerActive = buyerOrders.filter(order => !['completed', 'refunded', 'cancelled'].includes(order.buyer_status || order.status)).length;
  const buyerCompleted = buyerOrders.filter(order => (order.buyer_status || order.status) === 'completed').length;
  const sellerActive = sellerOrders.filter(order => !['completed', 'refunded', 'cancelled'].includes(order.seller_status || order.status)).length;
  const sellerCompleted = sellerOrders.filter(order => (order.seller_status || order.status) === 'completed').length;
  const publishedServices = ownedServices.filter(service => service.status === 'published').length;

  return (
    <div className="dash-page">
      <div className="dash-container">
        <section className="dash-hero">
          <div>
            <p className="dash-kicker">{isZh ? '个人工作台' : 'Workspace'}</p>
            <h1>{copy.greeting}，{user?.username || (isZh ? '用户' : 'user')}</h1>
            <p>{copy.subtitle}</p>
          </div>
          <Link to="/tasks/create" className="dash-primary-action">{copy.publish}</Link>
        </section>

        <section className="dash-metrics" aria-label={isZh ? '关键数据' : 'Key metrics'}>
          {[
            { key: 'tasks', label: copy.totalTasks, value: stats.total || 0 },
            { key: 'users', label: copy.creators, value: stats.users || 0 },
            { key: 'pool', label: copy.pool, value: yuan(stats.pool) },
            { key: 'paid', label: copy.paid, value: yuan(stats.paid) },
          ].map(item => (
            <div className="dash-metric" key={item.key}>
              <span className={`dash-metric-mark ${item.key}`} />
              <div>
                <strong>{loading ? '-' : item.value}</strong>
                <span>{item.label}</span>
              </div>
            </div>
          ))}
        </section>

        <section className="dash-role-grid" aria-label={isZh ? '双角色概览' : 'Dual role overview'}>
          <article className="dash-role-card">
            <div className="dash-role-card-head">
              <div>
                <p>{copy.buyingTitle}</p>
                <h2>{copy.buyingTitle}</h2>
              </div>
              <Link to="/wallet">{copy.manageBuying}</Link>
            </div>
            <div className="dash-role-stats">
              <div><strong>{buyerOrders.length}</strong><span>{copy.orders}</span></div>
              <div><strong>{buyerActive}</strong><span>{copy.active}</span></div>
              <div><strong>{buyerCompleted}</strong><span>{copy.completed}</span></div>
            </div>
          </article>
          <article className="dash-role-card dash-role-card-selling">
            <div className="dash-role-card-head">
              <div>
                <p>{copy.sellingTitle}</p>
                <h2>{copy.sellingTitle}</h2>
              </div>
              <Link to="/profile">{copy.manageSelling}</Link>
            </div>
            <div className="dash-role-stats">
              <div><strong>{publishedServices}</strong><span>{copy.services}</span></div>
              <div><strong>{sellerActive}</strong><span>{copy.active}</span></div>
              <div><strong>{sellerCompleted}</strong><span>{copy.completed}</span></div>
            </div>
          </article>
        </section>

        <div className="dash-grid">
          <main className="dash-main">
            <section className="dash-panel">
              <div className="dash-panel-head">
                <h2>{copy.recent}</h2>
                <Link to="/tasks">{copy.viewAll}</Link>
              </div>
              <div className="dash-task-list">
                {tasks.length ? tasks.map(task => (
                  <Link to={`/tasks/${task.id}`} className="dash-task-row" key={task.id}>
                    <div className="dash-task-main">
                      <strong>{task.title}</strong>
                      <span>{dateText(task.created_at, isZh)}</span>
                    </div>
                    <span className={`dash-status ${task.status || 'open'}`}>
                      {STATUS_COPY[language][task.status] || task.status}
                    </span>
                    <b>{yuan(task.budget_max)}</b>
                  </Link>
                )) : (
                  <div className="dash-empty">{copy.empty}</div>
                )}
              </div>
            </section>

            <section className="dash-panel">
              <div className="dash-panel-head">
                <h2>{copy.finance}</h2>
                <Link to="/wallet">{copy.viewAll}</Link>
              </div>
              <div className="dash-finance">
                <div>
                  <span>{copy.balance}</span>
                  <strong>{yuan(wallet.balance / 100)}</strong>
                </div>
                <div>
                  <span>{copy.escrow}</span>
                  <strong>{yuan(wallet.frozen / 100)}</strong>
                </div>
              </div>
            </section>
          </main>

          <aside className="dash-side">
            <section className="dash-panel">
              <h2>{copy.activity}</h2>
              {wallet.recent?.length ? (
                <div className="dash-activity">
                  {wallet.recent.slice(0, 4).map(item => (
                    <div className="dash-activity-row" key={item.id}>
                      <span>{item.reason || item.type}</span>
                      <strong>{yuan((item.amount || 0) / 100)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="dash-empty compact">{copy.noActivity}</div>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
